import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  SITE_NAME,
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  getSiteOrigin,
} from '../lib/siteMeta';

function upsertMeta(attr, key, content) {
  const sel = `meta[${attr}="${key}"]`;
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  const sel = `link[rel="${rel}"]`;
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Met à jour title, meta description, Open Graph, Twitter, geo (France) et canonical.
 */
export default function Seo({ title, description, image, canonicalPath, noindex = false }) {
  const { pathname, search } = useLocation();
  const pathForCanonical = canonicalPath ?? `${pathname}${search}`;

  useEffect(() => {
    const base = getSiteOrigin();
    const canonical = `${base}${pathForCanonical.startsWith('/') ? pathForCanonical : `/${pathForCanonical}`}`;
    const pageTitle = (title || SITE_NAME).trim();
    const fullTitle = pageTitle === SITE_NAME ? SITE_NAME : `${pageTitle} | ${SITE_NAME}`;
    const desc = (description || DEFAULT_DESCRIPTION).slice(0, 320);

    document.title = fullTitle;

    upsertMeta('name', 'description', desc);
    upsertMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large');

    upsertMeta('name', 'geo.region', 'FR');
    upsertMeta('name', 'geo.placename', 'France');
    upsertMeta('name', 'language', 'French');

    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', 'fr_FR');

    const ogImage = image || DEFAULT_OG_IMAGE;
    const imageUrl = ogImage.startsWith('http') ? ogImage : `${base}${ogImage.startsWith('/') ? ogImage : `/${ogImage}`}`;
    upsertMeta('property', 'og:image', imageUrl);
    upsertMeta('property', 'og:image:alt', pageTitle);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', imageUrl);

    upsertLink('canonical', canonical);

    let hreflang = document.querySelector('link[rel="alternate"][hreflang="fr-FR"]');
    if (!hreflang) {
      hreflang = document.createElement('link');
      hreflang.setAttribute('rel', 'alternate');
      hreflang.setAttribute('hreflang', 'fr-FR');
      document.head.appendChild(hreflang);
    }
    hreflang.setAttribute('href', canonical);

    let xdef = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (!xdef) {
      xdef = document.createElement('link');
      xdef.setAttribute('rel', 'alternate');
      xdef.setAttribute('hreflang', 'x-default');
      document.head.appendChild(xdef);
    }
    xdef.setAttribute('href', canonical);
  }, [title, description, image, pathForCanonical, noindex, pathname, search]);

  return null;
}
